import styled from 'styled-components'
import {
  ChartIcon,
  Flex,
  Heading,
  HistoryIcon,
  IconButton,
  NotificationDot,
  Text,
  useModal,
  ChartDisableIcon,
} from '@pancakeswap/uikit'
import TransactionsModal from 'components/App/Transactions/TransactionsModal'
import GlobalSettings from 'components/Menu/GlobalSettings'
import { useExpertModeManager } from 'state/user/hooks'
import RefreshIcon from 'components/Svg/RefreshIcon'

interface Props {
  title: string
  subtitle: string
  noConfig?: boolean
  setIsChartDisplayed?: React.Dispatch<React.SetStateAction<boolean>>
  isChartDisplayed?: boolean
  hasAmount: boolean
  onRefreshPrice: () => void
}

const CurrencyInputContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  width: 100%;
  background: linear-gradient(135deg, rgba(32, 153, 80, 0.1) 0%, rgba(1, 5, 29, 0.05) 100%);
  border-bottom: 2px solid rgba(32, 153, 80, 0.2);
  border-radius: 24px 24px 0 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, rgba(32, 153, 80, 0.5) 50%, transparent 100%);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(32, 153, 80, 0.03) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`

const ColoredIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.textSubtle};
  background: rgba(32, 153, 80, 0.1);
  border: 1px solid rgba(32, 153, 80, 0.2);
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  pointer-events: auto;
  
  &:hover {
    background: rgba(32, 153, 80, 0.2);
    border-color: rgba(32, 153, 80, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(32, 153, 80, 0.2);
  }
`

const StyledHeading = styled(Heading)`
  color: #209950;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.5px;
  text-shadow: 0 0 30px rgba(32, 153, 80, 0.3);
  position: relative;
  z-index: 1;
`

const StyledSubtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.textSubtle};
  font-size: 16px;
  font-weight: 500;
  opacity: 0.8;
  position: relative;
  z-index: 1;
`

const CurrencyInputHeader: React.FC<Props> = ({
  title,
  subtitle,
  setIsChartDisplayed,
  isChartDisplayed,
  hasAmount,
  onRefreshPrice,
}) => {
  const [expertMode] = useExpertModeManager()
  const toggleChartDisplayed = () => {
    setIsChartDisplayed((currentIsChartDisplayed) => !currentIsChartDisplayed)
  }
  const [onPresentTransactionsModal] = useModal(<TransactionsModal />)

  return (
    <CurrencyInputContainer>
      <Flex width="100%" alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" justifyContent="flex-start" flex="1" minWidth="0">
          {setIsChartDisplayed && (
            <ColoredIconButton onClick={toggleChartDisplayed} variant="text" scale="sm">
              {isChartDisplayed ? <ChartDisableIcon color="textSubtle" /> : <ChartIcon width="24px" color="textSubtle" />}
            </ColoredIconButton>
          )}
        </Flex>
        <Flex flexDirection="column" alignItems="center" justifyContent="center" flex="1" minWidth="0">
          <StyledHeading as="h2">{title}</StyledHeading>
        </Flex>
        <Flex alignItems="center" justifyContent="flex-end" flex="1" minWidth="0" style={{ gap: '8px' }}>
          <NotificationDot show={expertMode}>
            <ColoredIconButton variant="text" scale="sm">
              <GlobalSettings color="textSubtle" />
            </ColoredIconButton>
          </NotificationDot>
          <ColoredIconButton onClick={onPresentTransactionsModal} variant="text" scale="sm">
            <HistoryIcon color="textSubtle" width="24px" />
          </ColoredIconButton>
          <ColoredIconButton variant="text" scale="sm" onClick={() => onRefreshPrice()}>
            <RefreshIcon disabled={!hasAmount} color="textSubtle" width="27px" />
          </ColoredIconButton>
        </Flex>
      </Flex>
      <Flex alignItems="center" mt="8px">
        <StyledSubtitle>
          {subtitle}
        </StyledSubtitle>
      </Flex>
    </CurrencyInputContainer>
  )
}

export default CurrencyInputHeader
